import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trash2, Eye } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLoading } from "../context/LoadingContext";
import PageHeader from "../components/PageHeader";
import UserDetailDrawer from "../components/UserDetailDrawer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { FaDiscord } from "react-icons/fa";

export default function Staff() {
  const [staff, setStaff] = useState(null);
  const [roles, setRoles] = useState([]);
  const [domains, setDomains] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", workspace_local: "", workspace_domain: "", personal_email: "", avatar_url: "", role_id: "" });
  const { hasPerm } = useAuth();
  const { withLoading } = useLoading();

  const load = async () => {
    const [s, r, set] = await Promise.all([api.get("/staff"), api.get("/roles"), api.get("/settings")]);
    setStaff(s.data); setRoles(r.data); setDomains(set.data.email_domains);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.first_name || !form.last_name || !form.workspace_local || !form.workspace_domain || !form.personal_email) return toast.error("Fill all required fields");
    try {
      await withLoading(api.post("/staff", form), "Adding staff member");
      toast.success("Staff member added");
      setOpen(false);
      setForm({ first_name: "", last_name: "", workspace_local: "", workspace_domain: "", personal_email: "", avatar_url: "", role_id: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to add staff"); }
  };

  const remove = async (id) => {
    await withLoading(api.delete(`/staff/${id}`), "Removing staff");
    toast.success("Staff removed"); load();
  };

  return (
    <div>
      <PageHeader
        title="Staff Management"
        subtitle="Your Kingdom's team. Add members, assign roles, and manage access."
        action={hasPerm("manage_staff") && (
          <Button data-testid="add-staff-btn" onClick={() => setOpen(true)} className="gap-2 bg-purple-600 hover:bg-purple-500">
            <Plus className="h-4 w-4" /> Add Staff
          </Button>
        )}
      />

      {!staff ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl bg-slate-800/50" />)}</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {staff.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.04 }}
              className="card-glass group rounded-2xl p-5 transition-all hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(139,92,246,0.2)]"
              data-testid={`staff-card-${s.id}`}
            >
              <div className="flex items-center gap-3">
                <Avatar className="h-12 w-12 border border-purple-500/40">
                  <AvatarImage src={s.avatar_url || s.discord_avatar} />
                  <AvatarFallback className="bg-purple-600/30 text-purple-200">{`${s.first_name[0]}${s.last_name[0]}`}</AvatarFallback>
                </Avatar>
                <div className="min-w-0">
                  <p className="truncate font-semibold text-white">{s.first_name} {s.last_name}</p>
                  {s.role && <span className="rounded px-2 py-0.5 text-xs font-medium" style={{ background: `${s.role.color}22`, color: s.role.color }}>{s.role.name}</span>}
                </div>
              </div>
              <div className="mt-4 space-y-1 text-xs text-slate-400">
                <p className="truncate">{s.workspace_email}</p>
                <p className="flex items-center gap-1">{s.discord_username ? <><FaDiscord className="text-indigo-400" /> {s.discord_username}</> : <span className="text-slate-600">Discord not linked</span>}</p>
                {s.suspended && <span className="inline-block rounded bg-red-500/10 px-2 py-0.5 text-red-400">Suspended</span>}
              </div>
              <div className="mt-4 flex gap-2">
                <Button data-testid={`view-staff-${s.id}`} size="sm" variant="secondary" onClick={() => setDetailId(s.id)} className="flex-1 gap-1"><Eye className="h-3.5 w-3.5" /> View</Button>
                {hasPerm("manage_staff") && <Button data-testid={`delete-staff-${s.id}`} size="sm" variant="ghost" onClick={() => remove(s.id)} className="text-red-400 hover:bg-red-500/10"><Trash2 className="h-3.5 w-3.5" /></Button>}
              </div>
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-800 bg-[#121620] text-slate-100" data-testid="add-staff-dialog">
          <DialogHeader><DialogTitle>Add Staff Member</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">First Name *</Label><Input data-testid="staff-first-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="mt-1 bg-slate-900/60" /></div>
            <div><Label className="text-xs">Last Name *</Label><Input data-testid="staff-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="mt-1 bg-slate-900/60" /></div>
            <div className="col-span-2">
              <Label className="text-xs">Workspace Email *</Label>
              <div className="mt-1 flex gap-2">
                <Input data-testid="staff-workspace-local" placeholder="username" value={form.workspace_local} onChange={(e) => setForm({ ...form, workspace_local: e.target.value })} className="bg-slate-900/60" />
                <Select value={form.workspace_domain} onValueChange={(v) => setForm({ ...form, workspace_domain: v })}>
                  <SelectTrigger data-testid="staff-workspace-domain" className="w-[180px] bg-slate-900/60"><SelectValue placeholder="@domain" /></SelectTrigger>
                  <SelectContent className="border-slate-800 bg-[#181D2A] text-slate-100">
                    {domains.map((d) => <SelectItem key={d} value={d}>@{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="col-span-2"><Label className="text-xs">Personal Email * <span className="text-slate-500">(Discord match)</span></Label><Input data-testid="staff-personal-email" value={form.personal_email} onChange={(e) => setForm({ ...form, personal_email: e.target.value })} className="mt-1 bg-slate-900/60" /></div>
            <div className="col-span-2"><Label className="text-xs">Profile Picture URL (optional)</Label><Input data-testid="staff-avatar" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} className="mt-1 bg-slate-900/60" /></div>
            <div className="col-span-2">
              <Label className="text-xs">Role</Label>
              <Select value={form.role_id} onValueChange={(v) => setForm({ ...form, role_id: v })}>
                <SelectTrigger data-testid="staff-role" className="mt-1 bg-slate-900/60"><SelectValue placeholder="Select a role" /></SelectTrigger>
                <SelectContent className="border-slate-800 bg-[#181D2A] text-slate-100">
                  {roles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="submit-staff-btn" onClick={submit} className="bg-purple-600 hover:bg-purple-500">Add Member</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UserDetailDrawer open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} userType="staff" userId={detailId} onChanged={load} />
    </div>
  );
}
